import React, { PropsWithChildren, useEffect } from 'react';
import { Outlet, useLocation, useNavigation } from 'react-router-dom';
import Header from '../header';
import Footer from '../footer';
import { ChakraProvider, Box, BoxProps, Fade } from '@chakra-ui/react';
import theme from '@/theme';
import 'swiper/css';
import { SKIP_FOOTER_PATHS, SKIP_LAYOUT_PATHS } from '@/router';
import CartProvider from '@/app/cart/context/cart';
import ErrorBoundary from './error-boundary';
import { storage } from '@/utils/storage';
import goftino from '@/utils/goftino';
import { useSSRContext } from '@/ssr-context';
import { useOnlyClient } from '@/hooks/use-only-client';
import { Helmet } from '@/utils/helmet';
import PageLoading from '@/app/editor/components/ui/page-loader';

const Container = React.forwardRef(
  ({ children, wide, ...rest }: BoxProps & { wide?: boolean }, ref: any) => {
    return (
      <Box
        ref={ref}
        px={wide == null ? { base: 4, sm: 8, md: 10, lg: 12, xl: 24 } : 0}
        {...(wide && {
          position: 'relative',
          right: { base: -4, sm: -8, md: -10, lg: -12, xl: -24 },
          w: '100vw',
        })}
        {...rest}
      >
        {children}
      </Box>
    );
  },
);

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    document.body.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, [pathname]);
  return null;
}

function Layout() {
  const pathname = useLocation().pathname;
  const { state } = useNavigation();
  const ssr = useSSRContext();
  const render = useOnlyClient(ssr.isRouteSSR);
  const skipLayout = SKIP_LAYOUT_PATHS.includes(pathname);
  const skipFooter = SKIP_FOOTER_PATHS.includes(pathname);

  goftino.useInit();

  useEffect(() => {
    const user = storage.local.user.get();
    if (user) {
      goftino.setUser({
        name: user.addresses?.[0]?.name || user.mobileNumber,
        phone: user.mobileNumber,
      });
    }
  }, []);

  useEffect(() => {
    goftino.close();
  }, [pathname]);

  if (!render) {
    // eslint-disable-next-line
    return <></>;
  }
  if (skipLayout) {
    return (
      <Providers>
        <Head />
        <main style={{ height: '100%', flex: 1 }}>
          {state === 'loading' && <PageLoading overlay />}
          <Fade
            in={state != 'loading'}
            transition={{ enter: { duration: 0.1 }, exit: { duration: 0 } }}
            key={pathname}
            style={{ height: '100%' }}
          >
            <Outlet />
          </Fade>
        </main>
      </Providers>
    );
  }

  return (
    <Providers>
      <Head />
      <Header />
      <Container pt={{ base: 10, md: 16 }} flex={1}>
        <main>
          {state === 'loading' && <PageLoading overlay />}
          <Fade
            in={state != 'loading'}
            transition={{ enter: { duration: 0.1 }, exit: { duration: 0 } }}
            key={pathname}
            style={{ height: '100%' }}
          >
            <Outlet />
          </Fade>
        </main>
      </Container>
      {!skipFooter && <Footer />}
    </Providers>
  );
}

function Head() {
  return (
    <Helmet>
      <title>پینتورا | چاپ طرح دلخواه بر روی لباس و انواع محصولات</title>
      <meta
        name="description"
        content="چاپ عکس و طرح روی محصولات مختلف با کیفیت بالا | هزاران طرح آماده برای مناسبت‌های مختلف | چاپ تکی و عمده | ارسال به سراسر ایران | چاپ روی تیشرت، هودی، لیوان، و بیش از ۲۰ محصول دیگر"
      />
      <meta property="og:locale" content="fa_IR" />
      <meta property="og:type" content="website" />
      <meta
        property="og:title"
        content="چاپ طرح دلخواه بر روی لباس و انواع محصولات"
      />
      <meta property="og:url" content="https://pintora.ir" />
      <meta property="og:image" content="https://s3.pintora.ir/og-image.png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="908" />
      <meta
        property="og:description"
        content="چاپ عکس و طرح روی محصولات مختلف با کیفیت بالا | هزاران طرح آماده برای مناسبت‌های مختلف | چاپ تکی و عمده | ارسال به سراسر ایران | چاپ روی تیشرت، هودی، لیوان، و بیش از ۲۰ محصول دیگر"
      />
      <meta property="article:section" content="Printing Industry" />
      <meta property="article:tag" content="printing" />
      <meta property="article:tag" content="custom designs" />
      <meta property="article:tag" content="custom t-shirts" />
      <meta property="article:tag" content="custom hoodies" />
      <meta property="article:tag" content="custom mug" />
      <meta property="article:tag" content="sublimation printing" />
      <meta property="article:tag" content="dtf printing" />
    </Helmet>
  );
}

function Providers({ children }: PropsWithChildren<{}>) {
  return (
    <React.StrictMode>
      <ChakraProvider
        resetCSS={true}
        theme={theme}
        toastOptions={{
          defaultOptions: {
            containerStyle: {
              fontSize: 'sm',
              zIndex: 2000000003,
            },
          },
        }}
      >
        <ErrorBoundary>
          <CartProvider>{children}</CartProvider>
        </ErrorBoundary>
      </ChakraProvider>
      <ScrollToTop />
    </React.StrictMode>
  );
}

export { Container };
export default Layout;
